import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Radio from "antd/es/radio";
import { defineMessages, useIntl } from "react-intl";

import LeadFinderSubscriptionPlan, {
  LEAD_FINDER_ENABLED_PLANS,
} from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Button } from "@mapmycustomers/ui";

import upsellImage from "@app/assets/map/leadFinderUpsell.svg";
import LeadGenChecking from "@app/enum/LeadGenChecking";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import { LEAD_FINDER_UPSELL_URL } from "@app/scene/map/utils/consts";
import LeadGenSettings from "@app/scene/settings/type/LeadGenSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import {
  getLeadGenPlan,
  getOrganizationId,
  getOrganizationSettings,
  isCurrentUserOwner,
} from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useSetting from "@app/util/hook/useSetting";

import Entities from "./component/Entities";
import styles from "./LeadGenerationCommon.module.scss";

const messages = defineMessages({
  checkEntities: {
    id: "settings.organizationDetails.leadGen.checkEntities",
    defaultMessage:
      "When a user in your organization is searching for new opportunities with mobile lead generation, check for duplicates across:",
    description: "Settings organization details - Lead Generation - Check Entity description",
  },
  checkOrg: {
    id: "settings.organizationDetails.leadGen.check.organization",
    defaultMessage:
      "All records in the <b>organization</b> (whether or not they are visible to the user) ",
    description: "Settings organization details - Lead Generation - Check organization",
  },
  checkTeams: {
    id: "settings.organizationDetails.leadGen.check.teams",
    defaultMessage:
      "Only records in that <b>user’s team</b> (whether or not they are visible to the user) ",
    description: "Settings organization details - Lead Generation - Check teams",
  },
  checkUser: {
    id: "settings.organizationDetails.leadGen.check.user",
    defaultMessage: "Only records <b>owned by the user</b>",
    description: "Settings organization details - Lead Generation - Check user",
  },
  interested: {
    id: "settings.organizationDetails.leadGen.upsell.button.title",
    defaultMessage: "I’m Interested",
    description: "Interested button title",
  },
  purchaseDescription: {
    id: "settings.organizationDetails.leadGen.upsell.purchase.description",
    defaultMessage:
      "Find new leads by location, add companies directly from the map, sync new companies back to your CRM, and add extra data like hours, site, and contact info",
    description: "Purchase description",
  },
  purchaseTitle: {
    id: "settings.organizationDetails.leadGen.upsell.purchase.title",
    defaultMessage: "Add on <p>Lead Finder</p> to prospect from anywhere",
    description: "Purchase title",
  },
  upsellDescription: {
    id: "settings.organizationDetails.leadGen.upsell.upgrade.description",
    defaultMessage:
      "Upgrade from your basic lead gen for 3x results per search, panning map to get more results, and extra data like hours, site, and contact info.",
    description: "Upsell description",
  },
  upsellNote: {
    id: "settings.organizationDetails.leadGen.upsell.note",
    defaultMessage: "Tap “I’m interested” and we’ll have your account manager reach out.",
    description: "Upsell note",
  },
  upsellTitle: {
    id: "settings.organizationDetails.leadGen.upsell.upgrade.title",
    defaultMessage: "Get more from your lead results with <p>Lead Finder</p>",
    description: "Upsell title",
  },
});

interface Props {
  form: FormInstance<LeadGenSettings>;
  isOwner: boolean;
  leadGenPlan?: LeadFinderSubscriptionPlan;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: number;
  organizationSettings: Organization["settings"];
  setDirty: () => void;
  unsetDirty: () => void;
}

const LeadGeneration: React.FC<Props> = ({
  form,
  isOwner,
  leadGenPlan,
  onUpdateOrganization,
  organizationId,
  organizationSettings,
  setDirty,
  unsetDirty,
}) => {
  const intl = useIntl();

  const isBasicPlan = leadGenPlan === LeadFinderSubscriptionPlan.BASIC;

  const checkSetting = useSetting(organizationSettings, OrganizationSetting.LEAD_GEN_CHECK);
  const entitySetting = useSetting(organizationSettings, OrganizationSetting.LEAD_GEN_ENTITY);

  const handleSubmit = useCallback(() => {
    const { leadGenCheck, leadGenEntity } = form.getFieldsValue();
    if (organizationId) {
      onUpdateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          unsetDirty();
        },
        organization: { id: organizationId },
        settings: [
          checkSetting && leadGenCheck ? { ...checkSetting, value: leadGenCheck } : undefined,
          entitySetting && leadGenEntity ? { ...entitySetting, value: leadGenEntity } : undefined,
        ].filter(isDefined),
      });
    }
  }, [checkSetting, entitySetting, form, intl, organizationId, onUpdateOrganization, unsetDirty]);

  const handleUpgradeClick = useCallback(() => {
    window.open(LEAD_FINDER_UPSELL_URL, "_blank");
  }, []);

  return (
    <>
      {leadGenPlan && LEAD_FINDER_ENABLED_PLANS.includes(leadGenPlan) && (
        <Form
          form={form}
          initialValues={{
            leadGenCheck: checkSetting?.value,
            leadGenEntity: entitySetting?.value,
          }}
          onFieldsChange={setDirty}
          onFinish={handleSubmit}
        >
          <Form.Item name={OrganizationSetting.LEAD_GEN_ENTITY}>
            <Entities />
          </Form.Item>
          <div>
            <div>{intl.formatMessage(messages.checkEntities)}</div>

            <Form.Item name={OrganizationSetting.LEAD_GEN_CHECK}>
              <Radio.Group size="small">
                <div className={styles.options}>
                  <Radio value={LeadGenChecking.USER}>
                    {intl.formatMessage(messages.checkUser, { b: (text) => <b>{text}</b> })}
                  </Radio>
                  <Radio value={LeadGenChecking.TEAMS}>
                    {intl.formatMessage(messages.checkTeams, { b: (text) => <b>{text}</b> })}
                  </Radio>
                  <Radio value={LeadGenChecking.ORGANIZATION}>
                    {intl.formatMessage(messages.checkOrg, { b: (text) => <b>{text}</b> })}
                  </Radio>
                </div>
              </Radio.Group>
            </Form.Item>
          </div>
        </Form>
      )}
      {isOwner && leadGenPlan !== LeadFinderSubscriptionPlan.PRO && (
        <div className={styles.upsell}>
          <img alt="leadFinderUpsell" className={styles.image} src={upsellImage} />
          <div className={styles.content}>
            <span className={styles.title}>
              {intl.formatMessage(isBasicPlan ? messages.upsellTitle : messages.purchaseTitle, {
                p: (text: ReactNode[]) => <span className={styles.primary}>{text}</span>,
              })}
            </span>
            <span>
              {intl.formatMessage(
                isBasicPlan ? messages.upsellDescription : messages.purchaseDescription
              )}
            </span>
            <span className={styles.note}>{intl.formatMessage(messages.upsellNote)}</span>
          </div>
          <Button onClick={handleUpgradeClick} type="primary">
            {intl.formatMessage(messages.interested)}
          </Button>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  leadGenPlan: getLeadGenPlan(state),
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadGeneration);
